import {
  IssueLiabilityCostType,
  IssueLiabilityDoc,
  IssueLiabilityHistoryEntry,
  IssueLiabilityResponsibleType,
  IssueLiabilityStatus,
  issueLiabilityCostTypeMap,
  issueLiabilityResponsibleTypeMap,
  issueLiabilityStatusMap,
} from '@gooduncles/gu-app-schema';
import { ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Badge, Button, Popconfirm, Popover, Select, Tooltip, message } from 'antd';
import { formatDate } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';

import { formatNumberForAgCell, loadGridState } from 'src/utils/ag-grid-util';
import { formatNumber } from 'src/utils/common';
import { deleteIssueLiability } from 'src/utils/firebase/firebase-short-cut';
import { registeredAtValueFormatter } from 'src/utils/issue/issue-ag-grid-util';
import { issueLiabilityStatusBadgeMap, updateIssueLiabilityStatus } from 'src/utils/issue/issue-util';
import { AuditLogger } from 'src/utils/audit-logger';

import { formatDisplayName } from 'src/hooks/subscription/useStaffUsers';

const auditLogger = AuditLogger.getInstance();

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import { AgCellIssueLiabilityResponsibleDisplay } from 'src/atomic-components/atoms/AgCell/Issue/AgCellIssueLiabilityResponsibleDisplay';
import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';
import IssuePanelModal, { IssuePanelModalData } from 'src/components/Issue/IssuePanelModal';

export const ISSUE_LIABILITY_TABLE_ID = 'issue-liability-table';

const statusOptions = Object.entries(issueLiabilityStatusMap).map(([value, label]) => ({
  value: value as IssueLiabilityStatus,
  label,
}));

/** 상태 변경 cellRenderer (변경 시 히스토리에 자동 기록) */
const StatusSelectCellRenderer: FC<ICellRendererParams<IssueLiabilityDoc>> = (params) => {
  const { data, value } = params;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!data) return null;

  const handleStatusChange = async (newStatus: IssueLiabilityStatus) => {
    if (newStatus === value) return;

    setLoading(true);
    try {
      await updateIssueLiabilityStatus(
        data._id,
        value as IssueLiabilityStatus,
        newStatus,
        data.history,
        user?._id ?? '',
      );
      message.success('상태가 변경되었습니다.');
    } catch (error) {
      console.error('Failed to update status:', error);
      message.error('상태 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const currentBadge = issueLiabilityStatusBadgeMap[value as IssueLiabilityStatus];

  return (
    <Select
      value={value}
      onChange={handleStatusChange}
      options={statusOptions}
      size='small'
      loading={loading}
      disabled={loading}
      style={{ width: '100%' }}
      variant='borderless'
      labelRender={() => <Badge status={currentBadge?.status} text={currentBadge?.text} />}
      optionRender={(option) => {
        const optionBadge = issueLiabilityStatusBadgeMap[option.value as IssueLiabilityStatus];
        return <Badge status={optionBadge?.status} text={optionBadge?.text} />;
      }}
    />
  );
};

/** 삭제 버튼 cellRenderer (soft delete) */
const DeleteCellRenderer: FC<ICellRendererParams<IssueLiabilityDoc>> = (params) => {
  const { data } = params;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!data) return null;

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteIssueLiability(data._id, { audit: { suppress: true } });
      auditLogger.log({
        action: 'DELETE',
        entity: 'issue',
        entityId: data._id,
        summary: `손실 책임 삭제 - ${issueLiabilityCostTypeMap[data.costType]}, ${data.amount?.totalAmount ?? 0}원`,
        metadata: { costType: data.costType, amount: data.amount?.totalAmount },
      });
      message.success('삭제되었습니다.');
    } catch (error) {
      console.error('Failed to delete liability:', error);
      message.error('삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popconfirm
      title='책임 비용 삭제'
      description='정말 삭제하시겠습니까?'
      onConfirm={handleDelete}
      okText='삭제'
      cancelText='취소'
      okButtonProps={{ danger: true, loading }}
    >
      <Button type='link' size='small' danger loading={loading}>
        삭제
      </Button>
    </Popconfirm>
  );
};

/** 책임자 유형별 색상 */
const responsibleTypeColorMap: Record<IssueLiabilityResponsibleType, string> = {
  operator: '#1890ff',
  partners: '#722ed1',
  store: '#fa8c16',
  productSupplier: '#52c41a',
  etc: '#8c8c8c',
};

function responsibleTypeCellStyle(params: { value: IssueLiabilityResponsibleType }): { color: string } {
  return { color: responsibleTypeColorMap[params.value] };
}

interface IssueLiabilityTableProps {
  rowData: IssueLiabilityDoc[];
  onViewDetail: (liability: IssueLiabilityDoc) => void;
  toolbarLeftContent?: React.ReactNode;
}

const IssueLiabilityTable: FC<IssueLiabilityTableProps> = ({ rowData, onViewDetail, toolbarLeftContent }) => {
  const [gridRef, setGridRef] = useState<AgGridReact<IssueLiabilityDoc> | null>(null);
  const [issuePanelOpen, setIssuePanelOpen] = useState(false);
  const [issuePanelData, setIssuePanelData] = useState<IssuePanelModalData>(null);

  const handleOpenLinkedIssue = useCallback((issueCaseId: string) => {
    setIssuePanelData({ issueId: issueCaseId });
    setIssuePanelOpen(true);
  }, []);

  const handleCloseIssuePanel = useCallback(() => {
    setIssuePanelOpen(false);
    setIssuePanelData(null);
  }, []);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    [],
  );

  const columnDefs = useMemo<(ColDef<IssueLiabilityDoc> | ColGroupDef<IssueLiabilityDoc>)[]>(
    () => [
      {
        headerName: 'ID',
        field: '_id',
        hide: true,
      },
      {
        headerName: '등록일시',
        field: 'registeredAt',
        width: 120,
        valueFormatter: registeredAtValueFormatter,
        sort: 'desc',
      },
      {
        headerName: '상태',
        field: 'status',
        width: 152,
        cellRenderer: StatusSelectCellRenderer,
        filterParams: {
          valueFormatter: (params: { value: IssueLiabilityStatus }) =>
            issueLiabilityStatusMap[params.value] || params.value,
        },
      },
      {
        headerName: '비용 유형',
        field: 'costType',
        width: 120,
        valueFormatter: (params) => issueLiabilityCostTypeMap[params.value as IssueLiabilityCostType] || params.value,
      },
      {
        headerName: '책임자 유형',
        field: 'responsibleType',
        width: 130,
        valueFormatter: (params) =>
          issueLiabilityResponsibleTypeMap[params.value as IssueLiabilityResponsibleType] || params.value,
        cellStyle: responsibleTypeCellStyle,
      },
      {
        headerName: '책임자',
        field: 'responsible',
        width: 150,
        cellRenderer: AgCellIssueLiabilityResponsibleDisplay,
        cellRendererParams: (params: { data: IssueLiabilityDoc }) => ({
          responsibleType: params.data.responsibleType,
          responsibleId: params.data.responsibleId ?? undefined,
        }),
      },
      {
        headerName: '금액',
        children: [
          {
            headerName: '요약',
            width: 200,
            columnGroupShow: 'closed',
            cellRenderer: (params: { data: IssueLiabilityDoc }) => {
              const amount = params.data?.amount;
              if (!amount) return null;
              return (
                <span>
                  <strong>₩{formatNumber(amount.totalAmount)}</strong>
                  <span style={{ fontSize: 11, color: '#8c8c8c', marginLeft: 4 }}>
                    ({formatNumber(amount.supplyAmount)}+{formatNumber(amount.taxAmount)})
                  </span>
                </span>
              );
            },
            filter: false,
            sortable: false,
          },
          {
            headerName: '총액',
            field: 'amount.totalAmount',
            width: 120,
            columnGroupShow: 'open',
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
            cellStyle: { fontWeight: 600 },
          },
          {
            headerName: '공급가',
            field: 'amount.supplyAmount',
            width: 120,
            columnGroupShow: 'open',
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
          },
          {
            headerName: '세금',
            field: 'amount.taxAmount',
            width: 100,
            columnGroupShow: 'open',
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
          },
        ],
      },
      {
        headerName: '메모',
        field: 'memo',
        width: 200,
        tooltipField: 'memo',
      },
      {
        headerName: '진행 노트',
        field: 'progressNote',
        width: 150,
        tooltipField: 'progressNote',
      },
      {
        headerName: '기록',
        field: 'history',
        width: 80,
        cellRenderer: (params: { value: IssueLiabilityHistoryEntry[] | null }) => {
          const history = params.value ?? [];
          const count = history.length;

          if (count === 0) {
            return <span style={{ color: '#8c8c8c' }}>0건</span>;
          }

          const content = (
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {history.map((entry, idx) => {
                const mmdd = formatDate(entry.createdAt, 'MM.dd');
                return (
                  <div key={idx} style={{ whiteSpace: 'nowrap' }}>
                    • {mmdd} {entry.memo ?? '-'}
                  </div>
                );
              })}
            </div>
          );

          return (
            <Popover content={content} trigger='hover' placement='left'>
              <Button type='link' size='small'>
                {count}건
              </Button>
            </Popover>
          );
        },
      },
      {
        headerName: '연결 이슈',
        width: 100,
        cellRenderer: (params: { data: IssueLiabilityDoc }) => {
          if (!params.data?.issueCaseId) return null;
          return (
            <Tooltip title='연결된 이슈 보기'>
              <Button type='link' size='small' onClick={() => handleOpenLinkedIssue(params.data.issueCaseId)}>
                이슈 보기
              </Button>
            </Tooltip>
          );
        },
      },
      {
        headerName: '삭제',
        width: 80,
        cellRenderer: DeleteCellRenderer,
      },
      {
        headerName: '상세',
        width: 80,
        pinned: 'right',
        cellRenderer: (params: { data: IssueLiabilityDoc }) => {
          if (!params.data) return null;
          return (
            <Tooltip title='상세보기'>
              <Button type='link' size='small' onClick={() => onViewDetail(params.data)}>
                보기
              </Button>
            </Tooltip>
          );
        },
      },
      {
        headerName: '액션',
        hide: true,
        width: 120,
        cellRenderer: DownloadRawDataCell,
      },
    ],
    [onViewDetail, handleOpenLinkedIssue],
  );

  const handleGridReady = useCallback(() => {
    if (gridRef) {
      loadGridState(gridRef, ISSUE_LIABILITY_TABLE_ID);
    }
  }, [gridRef]);

  return (
    <>
      <div className='height100 flexColumn' style={{ gap: 8 }}>
        <div className='flexRow flexAlignCenter flexSpaceBetween'>
          {toolbarLeftContent}
          <AgGridSettingsButtons gridRef={gridRef} tableId={ISSUE_LIABILITY_TABLE_ID} />
        </div>
        <div className='ag-theme-alpine height100 width100'>
          <AgGridReact
            ref={setGridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={handleGridReady}
            enableCellTextSelection={true}
            tooltipShowDelay={500}
            getRowId={(params) => params.data._id}
          />
        </div>
      </div>
      {/* 연결된 이슈 상세 모달 */}
      {issuePanelData && <IssuePanelModal open={issuePanelOpen} onClose={handleCloseIssuePanel} {...issuePanelData} />}
    </>
  );
};

export default IssueLiabilityTable;
